import WarningTriangleGrayIcon from '@/assets/icons/V3/WarningTriangleGrayIcon.svg?react';
import { Button } from '@/components/V3/Button';
import Footer from '@/components/organisms/Footer';
import { GA_ROUTES } from '@/constants/ga';
import { ENTERED_BY_MARKET_NAME } from '@/constants/localstorage';
import { SigninContainer } from '@/containers/auth/signin-container';
import useWebview from '@/hooks/V3/useWebview';
import { usePageView } from '@/hooks/use-page-view';
import { useResponsive } from '@/hooks/useResponsive';
import { ROUTES } from '@/routes';
import { AES } from '@/utils/AES';
import { useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { AUTH_PAGE_CONTENT_CLASS_NAME } from './auth-layout';

const SignInPage = () => {
  const navigate = useNavigate();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const autoClose = urlSearchParams.get('autoclose') === 'true';
  const redirectPath = urlSearchParams.get('redirect');

  const { isWebviewError } = useResponsive();
  const { postMessage } = useWebview();
  const [cplatToken, setCplatToken] = useLocalStorage('cplatToken', '');

  const enteredByMarketNameRef = useRef(localStorage.getItem(ENTERED_BY_MARKET_NAME));
  const initialTokenRef = useRef(!!localStorage.getItem('cplatToken'));
  useLayoutEffect(() => {
    if (!cplatToken || cplatToken === '') return;

    // autoClose: 최초 진입 시 이미 토큰이 있는 경우에만 처리
    // (로그인 폼에서 토큰 저장 후에는 폼 컴포넌트에서 직접 처리)
    if (autoClose && initialTokenRef.current) {
      if (window.opener || window.history.length <= 1) {
        window.close();
      }
      navigate(ROUTES.dashboard, { replace: true });
      return;
    }

    // 방금 로그인한 경우 (최초 진입 시 토큰 없었음) → 폼 컴포넌트에서 직접 navigate 처리
    if (!initialTokenRef.current) return;

    const rawToken = cplatToken.replace(/^['"]|['"]$/g, '');
    const isJwt = rawToken.split('.').length === 3;
    const isEncrypted = rawToken.startsWith('U2FsdGVk');

    // 암호화된 토큰인 경우 -> 복호화 후 저장하고 이동
    if (isEncrypted && !isJwt) {
      try {
        const decrypted = AES.decryptData(rawToken);
        const cleanDecrypted = decrypted.replace(/^['"]|['"]$/g, '');

        if (cleanDecrypted && cleanDecrypted.split('.').length === 3) {
          setCplatToken(cleanDecrypted);
          if (!enteredByMarketNameRef.current) {
            navigate(redirectPath || ROUTES.dashboard, { replace: true });
          }
          return;
        }
      } catch (e) {
        console.warn('Failed to decrypt token. Clearing.');
        setCplatToken('');
        return;
      }
    }

    if (!isJwt) {
      setCplatToken('');
      return;
    }

    // 정상 JWT인 경우 -> 대시보드로 이동 (replace로 히스토리에 /login 안 남김)
    if (cplatToken !== rawToken) {
      setCplatToken(rawToken);
    }
    if (!enteredByMarketNameRef.current)
      navigate(redirectPath || ROUTES.dashboard, { replace: true });
  }, [autoClose, cplatToken, navigate, redirectPath, setCplatToken]);

  usePageView(GA_ROUTES.login);

  return (
    <>
      {isWebviewError ? (
        // webview 토큰 에러 발생 시, app 로그인 화면으로 reset 이동시키기
        <div className="flex h-[100dvh] flex-col items-center justify-center gap-[20px]">
          <WarningTriangleGrayIcon />
          <div className="whitespace-pre text-center text-gr-700 Body1S16">{`일시적인 오류가 발생했습니다\n잠시 후 다시 이용해 주세요`}</div>
          <Button
            variant={'SolidLow'}
            height={'44'}
            className="w-[200px]"
            onClick={() => {
              postMessage({
                action: 'resetApp',
                data: {
                  index: 0,
                  routes: [
                    {
                      key: 'Start',
                      name: 'Start',
                    },
                  ],
                },
              });
            }}
          >
            다시 로그인하기
          </Button>
        </div>
      ) : (
        <div className="scrollbar flex flex-col bg-gr-50 max-md:bg-white">
          <div className={AUTH_PAGE_CONTENT_CLASS_NAME}>
            <SigninContainer />
          </div>
          <Footer />
        </div>
      )}
    </>
  );
};

export default SignInPage;
